import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatISO, isAfter, startOfDay } from "date-fns";
import { Box, Button, Card, CardContent, CardHeader, Grid, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useFormik } from "formik";
import { get, orderBy } from "lodash";
import queryString from "query-string";
import * as Yup from "yup";
import makeStyles from "@mui/styles/makeStyles";

import { CommonDateRangeSelectionButtons, CommonTextField, Page } from "@APP/components";
import { NUMBER_OF_FIRST_PAGE_IN_TABLE_WITH_PAGINATION, commonFormatDate } from "@APP/constants";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";
import { AppState, hideLoader, setRTPS, setRtpFrom, setRtpTo, showLoader } from "@APP/redux";
import { API } from "@APP/services";
import { AdminRTPFilter, RTP, RtpSortBy, SortType } from "@APP/types";
import { formatErrorMessage } from "@APP/utils";

import RTPDashboardList from "./RTPDashboardList";

export type RTPWithDisplaying = RTP & { hidden: boolean };

enum RtpSortPaths {
  dueDateTime = "receivable.dueDateTime",
  createdAt = "createdAt",
  createdAtTime = "createdAt",
  amount = "amount.amount",
  status = "status",
  email = "supplier.primaryContact.email",
  firstDeliveryDate = "deliveryDates",
  firstDeliveryTime = "deliveryDates",
  channel = "channel",
}

export const adminRTPValidationSchema = Yup.object().shape({
  from: Yup.date().required("Start date is required"),
  to: Yup.date().test("to", "End date must be after start date", (value, context) => {
    if (!value) return false;
    // start date can be the same as end date
    if (value.getTime() === context.parent.from.getTime()) return true;
    if (!context.parent.from) return false;

    return isAfter(value, context.parent.from);
  }),
});

const useStyles = makeStyles((theme) => ({
  buttonsContainer: {
    marginTop: theme.spacing(0.5),
  },
}));

const RTPDashboardView = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const classes = useStyles();
  const handleErrorCodes = useHandleErrorCodes();
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [customDatePicker, setCustomDatePicker] = useState(false);
  const [entries, setEntries] = useState(25);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [lastPage, setLastPage] = useState(0);
  const [rtpSort, setRtpSort] = useState<RtpSortBy>(RtpSortBy.createdAt);
  const [sortedRtps, setSortedRtps] = useState<RTPWithDisplaying[]>([]);
  const [sortType, setSortType] = useState<SortType>(SortType.asc);
  const [fromLabel, setFromLabel] = useState<string>("");
  const { data, from, to } = useSelector(
    ({
      userAdmin: {
        rtpDashboard: { from, to, data },
      },
    }: AppState) => ({
      from,
      to,
      data,
    }),
  );

  const appendHiddenToRtp = (rtps: RTP[]): RTPWithDisplaying[] =>
    rtps.map((rtp) => ({ ...rtp, hidden: false }));

  useEffect(() => {
    !!data.length && setSortedRtps(appendHiddenToRtp(data));
  }, []);

  const getRtpsInDateRangeRecursively = async (
    filter: AdminRTPFilter,
    page = 0,
    prevRtps: RTP[] = [],
  ): Promise<RTP[]> => {
    const { data: rtpData, links, meta } = await API.getRTPSinDateRange(filter, { page });
    const rtps = [...prevRtps, ...rtpData] as RTP[];

    if (links.next) {
      return getRtpsInDateRangeRecursively(filter, page + 1, rtps);
    }
    const { query } = queryString.parseUrl(links?.last ?? "");
    setTotalItems(meta.totalItems);
    if (meta.totalItems) {
      if (meta.totalItems <= entries) {
        setLastPage(1);
      } else {
        if (query.page) {
          const lastPageIndex = Math.ceil(meta.totalItems / entries);
          setLastPage(Math.floor(lastPageIndex));
        }
      }
    }

    return rtps;
  };

  const fetchRTPs = async ({ from, to }: { from: string; to: string }) => {
    try {
      dispatch(showLoader());
      const rtpsData = await getRtpsInDateRangeRecursively({ from, to });

      dispatch(setRTPS(rtpsData));
      setSortedRtps(appendHiddenToRtp(rtpsData));
    } catch (error: any) {
      const errorData = error?.response?.data;
      const isHandled = handleErrorCodes(errorData?.errorCode);

      if (!isHandled) return;
      alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleSorting = (by: RtpSortBy) => {
    const newSortType = by === rtpSort && sortType === SortType.asc ? SortType.desc : SortType.asc;

    if (by === rtpSort) {
      // Reverse the array manually
      const reversedRtps = [...sortedRtps].reverse();
      setSortedRtps(reversedRtps);
    } else {
      const rtpSortField = RtpSortPaths[by];
      const rtpsWithoutSortingField = sortedRtps.filter((rtp) => !get(rtp, rtpSortField));
      const rtpsWithSortingField = sortedRtps.filter((rtp) => get(rtp, rtpSortField));

      const sortedRtpsWithSortingField = orderBy(
        rtpsWithSortingField,
        (rtp) => get(rtp, rtpSortField),
        newSortType,
      );

      setSortedRtps([...sortedRtpsWithSortingField, ...rtpsWithoutSortingField]);
    }

    setRtpSort(by);
    setSortType(newSortType);
  };

  const searchData = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.toLowerCase();
      if (!value) {
        setSortedRtps(sortedRtps.map((rtp) => ({ ...rtp, hidden: false })));

        return;
      }

      const filteredRtps = sortedRtps.map((rtp) => {
        const {
          amount: { amount },
          createdAt,
          status,
          supplier: { companyInfo: { email = "", name = "" } = {} },
        } = rtp;

        const filterOn = [createdAt, amount, status, email, name];
        if (filterOn.some((filter) => filter?.toString().toLowerCase().includes(value))) {
          return { ...rtp, hidden: false };
        }

        return { ...rtp, hidden: true };
      });

      setSortedRtps(filteredRtps);
    },
    [sortedRtps],
  );

  const handleOnEntriesChange = (entries: number) => {
    setPage(NUMBER_OF_FIRST_PAGE_IN_TABLE_WITH_PAGINATION);
    setEntries(entries);
    setLastPage(Math.ceil(totalItems / entries));
  };

  const clearSearchFilter = () =>
    setSortedRtps(sortedRtps.map((rtp) => ({ ...rtp, hidden: false })));

  const handleOnSubmit = ({ from, to }: { from: string; to: string }) => {
    dispatch(setRtpFrom(from));
    dispatch(setRtpTo(to));
    fetchRTPs({ from, to });
    setSubmitting(false);
    setPage(0);
  };

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    touched,
    values,
    setFieldValue,
    resetForm,
    setSubmitting,
  } = useFormik({
    initialValues: {
      from: from,
      to: to,
      username: "",
    },
    validationSchema: adminRTPValidationSchema,
    onSubmit: handleOnSubmit,
  });

  useEffect(() => {
    const from = formatISO(startOfDay(new Date()));
    const to = formatISO(new Date());
    fetchRTPs({ from, to });
    setFromLabel("Today");
  }, []);

  const orgPagination = { page: page, entries: entries };

  const fetchSearchResults = async (fromLabel: string) => {
    dispatch(showLoader());
    try {
      if (entries !== 25) {
        showAlert(
          "Search Error",
          "There are too many records for this search criteria. Please update the search to be more specific.",
        );

        return;
      }

      const userNameRegex = /@[^\s@]*\.com\b/;
      const isEmailFormat = userNameRegex.test(values.username);

      if (isEmailFormat) {
        await handleUserNameSearch(fromLabel);
      } else {
        await handleOrganizationSearch(fromLabel);
      }
    } catch (error) {
      showAlert("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleUserNameSearch = async (fromLabel: string) => {
    dispatch(showLoader());
    try {
      const fromDate = fromLabel === "Today" ? formatISO(startOfDay(new Date())) : from;

      const searchedData = await API.getPaymentsFromUserName(fromDate, values.username);
      if (searchData.length) {
        setSortedRtps(searchedData);
      } else {
        setSortedRtps([]);
      }
    } catch (error) {
      showAlert("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleOrganizationSearch = async (fromLabel: string) => {
    dispatch(showLoader());
    try {
      const orgData = await API.getSearchedOrganisations(
        { orgname: values.username },
        orgPagination,
      );

      const orgIds = orgData.data.map((org) => org.orgId);

      if (!orgIds.length) {
        setSortedRtps([]);

        return;
      }

      const fromDate = fromLabel === "Today" ? formatISO(startOfDay(new Date())) : from;

      const searchedData = await API.getPaymentsFromUserOrg(fromDate, orgIds);
      if (searchData.length) {
        setSortedRtps(searchedData);
      }
    } catch (error) {
      showAlert("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const showAlert = (title: string, message: string) => {
    alert.open(title, message, [{ text: "Okay" }]);
  };

  const clearResults = () => {
    resetForm();
    window.location.reload();
  };

  return (
    <Page title="RTP Dashboard">
      <form onSubmit={handleSubmit}>
        <Card elevation={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box p={2}>
                <Grid container item spacing={1}>
                  <Grid className={classes.buttonsContainer} item xs={12} md={8} lg={8}>
                    <CommonDateRangeSelectionButtons
                      fieldValueSetter={setFieldValue}
                      contentJustify="space-evenly"
                      values={values}
                      setCustomDatePicker={setCustomDatePicker}
                      setFromLabel={setFromLabel}
                    />
                  </Grid>
                  {customDatePicker && (
                    <Grid container item direction="column" xs={12} sm={4} md={4} spacing={1}>
                      <Grid style={{ display: "flex", gap: "8px" }} item>
                        <DatePicker
                          renderInput={(props) => (
                            <TextField
                              error={Boolean(touched.from && errors.from)}
                              helperText={touched.from && errors.from}
                              fullWidth
                              size="small"
                              onClick={() => setStartDatePickerOpen(true)}
                              {...props}
                            />
                          )}
                          onChange={(value) => {
                            setFieldValue("from", value);
                          }}
                          value={values.from}
                          label="Start Date"
                          inputFormat={commonFormatDate}
                          disableFuture
                          open={startDatePickerOpen}
                          onOpen={() => setStartDatePickerOpen(true)}
                          onClose={() => setStartDatePickerOpen(false)}
                        />
                        <DatePicker
                          renderInput={(props) => (
                            <TextField
                              error={Boolean(touched.to && errors.to)}
                              helperText={touched.to && errors.to}
                              fullWidth
                              size="small"
                              onClick={() => setEndDatePickerOpen(true)}
                              label="End Date"
                              {...props}
                            />
                          )}
                          onChange={(value) => {
                            setFieldValue("to", value);
                          }}
                          value={values.to}
                          label="End Date"
                          inputFormat={commonFormatDate}
                          disableFuture
                          open={endDatePickerOpen}
                          onOpen={() => setEndDatePickerOpen(true)}
                          onClose={() => setEndDatePickerOpen(false)}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Box>
              {customDatePicker && (
                <>
                  <Box p={2} style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Grid container item style={{ display: "contents" }}>
                      <Grid item lg={4} xs={false} />
                      <Grid item lg={4} xs={12}>
                        <Button
                          color="primary"
                          disabled={isSubmitting}
                          fullWidth
                          type="submit"
                          variant="contained">
                          View
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </Card>
        <Card
          sx={{ marginBottom: 6, marginTop: 6 }}
          id="rtp-search-card"
          role="search"
          aria-labelledby="search-card-title">
          <CardHeader
            sx={{ paddingBottom: 0 }}
            title="Search RTP's"
            id="search-card-title"
            titleTypographyProps={{
              "aria-level": 2,
              role: "heading",
            }}
          />
          <CardContent sx={{ paddingTop: 0 }}>
            <Grid
              container
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              role="form"
              aria-labelledby="search-card-title">
              <Grid item xs={9.5}>
                <CommonTextField
                  id="username-search-input"
                  disabled={isSubmitting}
                  error={Boolean(touched.username && errors.username)}
                  fullWidth
                  helperText={touched.username && errors.username}
                  label="Enter user name(email) or organization name"
                  margin="normal"
                  name="username"
                  onBlur={(e) => {
                    const trimmedValue = e.target.value.trim();
                    if (trimmedValue !== e.target.value) {
                      setFieldValue("username", trimmedValue);
                    }
                    handleBlur(e);
                  }}
                  onChange={handleChange}
                  type="text"
                  value={values.username}
                  variant="outlined"
                  aria-label="Enter username, email address, or organization name to search"
                  aria-describedby={
                    touched.username && errors.username
                      ? "username-error-text"
                      : "username-helper-text"
                  }
                  aria-invalid={Boolean(touched.username && errors.username)}
                  aria-required="true"
                  inputProps={{
                    "aria-label": "Username or organization search input",
                    "aria-describedby": "username-helper-text",
                  }}
                />
                <div id="username-helper-text" style={{ display: "none" }} aria-hidden="true">
                  Enter a username (email format) or organization name to search for RTP
                  transactions
                </div>
              </Grid>
              <Grid
                item
                xs={2}
                display="flex"
                gap={1}
                flexDirection="column"
                justifyContent="space-between"
                alignItems="center"
                role="group"
                aria-label="Search actions">
                <Button
                  id="search-submit-button"
                  color="primary"
                  fullWidth
                  disabled={!values.username?.trim() || isSubmitting}
                  onClick={() => fetchSearchResults(fromLabel)}
                  variant="contained"
                  aria-label={
                    isSubmitting ? "Searching for RTP transactions" : "Search for RTP transactions"
                  }
                  aria-describedby="search-button-description">
                  {isSubmitting ? "Searching..." : "Search"}
                </Button>
                <div id="search-button-description" style={{ display: "none" }} aria-hidden="true">
                  Click to search for RTP transactions based on the entered username or organization
                </div>

                {values.username?.trim() && (
                  <Button
                    id="reset-search-button"
                    type="reset"
                    disabled={isSubmitting}
                    onClick={clearResults}
                    fullWidth
                    color="primary"
                    variant="outlined"
                    aria-label="Reset search form and clear results"
                    aria-describedby="reset-button-description">
                    Reset
                  </Button>
                )}
                <div id="reset-button-description" style={{ display: "none" }} aria-hidden="true">
                  Click to clear the search input and reset all results
                </div>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <RTPDashboardList
          data={sortedRtps}
          rtpSort={rtpSort}
          sortType={sortType}
          entries={entries}
          page={page}
          lastPage={lastPage}
          setEntries={setEntries}
          setPage={setPage}
          clearSearchFilter={clearSearchFilter}
          searchData={searchData}
          handleSorting={handleSorting}
          handleOnEntriesChange={handleOnEntriesChange}
        />
      </form>
    </Page>
  );
};

export default RTPDashboardView;
